import React, { useState, useEffect } from 'react';
import { X, Folder, Image as ImageIcon, Check } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { resolveMediaUrl } from '../utils/mediaUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MediaPicker = ({ isOpen, onClose, onSelect, fileType = 'image' }) => {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('/');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
    }
  }, [isOpen, currentFolder]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API}/media/files?folder=${encodeURIComponent(currentFolder)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Filter by file type if specified
      let filteredFiles = response.data.files;
      if (fileType === 'image') {
        filteredFiles = filteredFiles.filter(f => f.mimeType.startsWith('image/'));
      }
      
      setFiles(filteredFiles);
      setFolders(response.data.folders);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMediaUrl = (relativePath) => resolveMediaUrl(relativePath);

  /**
   * Prefer optimized "large" variant when the media row has it.
   * Falls back to the original URL for unoptimized legacy files.
   */
  const getBestUrl = (file) => {
    if (!file) return '';
    const largeUrl = file.optimized?.large?.jpg?.url;
    if (largeUrl) return getMediaUrl(largeUrl);
    return getMediaUrl(file.url);
  };

  const handleSelect = () => {
    if (selectedFile) {
      onSelect(getBestUrl(selectedFile));
      onClose();
    }
  };

  const navigateToFolder = (folderPath) => {
    setCurrentFolder(folderPath);
    setSelectedFile(null);
  };

  const navigateUp = () => {
    if (currentFolder === '/') return;
    const parts = currentFolder.split('/').filter(Boolean);
    parts.pop();
    setCurrentFolder('/' + parts.join('/'));
    setSelectedFile(null);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Select Image from Gallery</h2>
            <p className="text-sm text-gray-600 mt-1">Choose an image for your slider</p>
          </div>
          <button onClick={onClose}>
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Breadcrumb */}
        <div className="px-6 py-3 bg-gray-50 border-b">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigateToFolder('/')}
              className="text-blue-600 hover:underline"
            >
              Home
            </button>
            {currentFolder.split('/').filter(Boolean).map((part, idx, arr) => (
              <React.Fragment key={idx}>
                <span>/</span>
                <button
                  onClick={() => {
                    const path = '/' + arr.slice(0, idx + 1).join('/');
                    navigateToFolder(path);
                  }}
                  className="text-blue-600 hover:underline"
                >
                  {part}
                </button>
              </React.Fragment>
            ))}
            {currentFolder !== '/' && (
              <Button
                onClick={navigateUp}
                variant="ghost"
                size="sm"
                className="ml-2"
              >
                ← Back
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : (
            <>
              {/* Folders */}
              {folders.length > 0 && (
                <div className="grid grid-cols-6 gap-4 mb-6">
                  {folders.map((folder) => (
                    <div
                      key={folder.id}
                      onClick={() => navigateToFolder(folder.path)}
                      className="cursor-pointer p-4 hover:bg-gray-50 rounded-lg border-2 border-dashed"
                    >
                      <Folder className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
                      <p className="text-sm text-center font-medium truncate">
                        {folder.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Files */}
              {files.length > 0 ? (
                <div className="grid grid-cols-4 gap-4">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      onClick={() => setSelectedFile(file)}
                      className={`cursor-pointer rounded-lg border-2 p-2 transition-all ${
                        selectedFile?.id === file.id
                          ? 'border-[#be1f1a] bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="relative">
                        {file.mimeType.startsWith('image/') ? (
                          <img
                            src={getMediaUrl(file.url)}
                            alt={file.originalName}
                            className="w-full h-40 object-cover rounded"
                          />
                        ) : (
                          <div className="w-full h-40 bg-gray-200 flex items-center justify-center rounded">
                            <ImageIcon className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                        {selectedFile?.id === file.id && (
                          <div className="absolute top-2 right-2 bg-[#be1f1a] text-white rounded-full p-1">
                            <Check className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                      <p className="text-sm font-medium truncate mt-2">
                        {file.originalName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  ))}
                </div>
              ) : folders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>No images found</p>
                  <p className="text-sm">Upload images to the media gallery first</p>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedFile ? (
              <span>Selected: <strong>{selectedFile.originalName}</strong></span>
            ) : (
              <span>Click on an image to select it</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSelect}
              disabled={!selectedFile}
              className="bg-[#be1f1a] hover:bg-[#a71b17]"
            >
              Select Image
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
